import { lazy, Suspense } from 'react'
import { BrowserRouter, Routes, Route, Navigate, Outlet } from 'react-router-dom'
import { LegacyPageSlugRedirects, PageSlugs, REMOVED_PAGE_SLUGS } from './constants/pageSlugs'
import { adminPath } from './constants/adminRoutes'
import { LanguageProvider } from './context/LanguageContext'
import { AuthProvider } from './context/AuthContext'
import MainLayout from './components/layout/MainLayout'
import LoadingSpinner from './components/ui/LoadingSpinner'
import Home from './pages/Home'
import PageTemplate from './pages/PageTemplate'
import NewsList from './pages/news/NewsList'
import NewsDetail from './pages/news/NewsDetail'
import EventsList from './pages/events/EventsList'
import EventDetail from './pages/events/EventDetail'
import GalleryPage from './pages/gallery/GalleryPage'
import ContactPage from './pages/contact/ContactPage'
import ApplyPage from './pages/apply/ApplyPage'
import CounselingPage from './pages/counseling/CounselingPage'
import AccreditationsPage from './pages/AccreditationsPage'
import WeeklySchedulePage from './pages/WeeklySchedulePage'
import CampusesPage from './pages/CampusesPage'
import ProgramsPage from './pages/ProgramsPage'
import CareersListPage from './pages/careers/CareersListPage'
import ScrollToTop from './components/routing/ScrollToTop'

const AdminLayout = lazy(() => import('./pages/admin/AdminLayout'))
const AdminLogin = lazy(() => import('./pages/admin/AdminLogin'))
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'))
const AdminPages = lazy(() => import('./pages/admin/AdminPages'))
const AdminNews = lazy(() => import('./pages/admin/AdminNews'))
const AdminEvents = lazy(() => import('./pages/admin/AdminEvents'))
const AdminSliders = lazy(() => import('./pages/admin/AdminSliders'))
const AdminGallery = lazy(() => import('./pages/admin/AdminGallery'))
const AdminSettings = lazy(() => import('./pages/admin/AdminSettings'))
const AdminMenus = lazy(() => import('./pages/admin/AdminMenus'))
const AdminApplications = lazy(() => import('./pages/admin/AdminApplications'))
const AdminApplicationDetail = lazy(() => import('./pages/admin/AdminApplicationDetail'))
const AdminCounseling = lazy(() => import('./pages/admin/AdminCounseling'))
const AdminCounselingDetail = lazy(() => import('./pages/admin/AdminCounselingDetail'))
const AdminHomepage = lazy(() => import('./pages/admin/AdminHomepage'))
const AdminUsers = lazy(() => import('./pages/admin/AdminUsers'))
const AdminActivityLogs = lazy(() => import('./pages/admin/AdminActivityLogs'))
const AdminProfile = lazy(() => import('./pages/admin/AdminProfile'))
const AdminFormsLayout = lazy(() => import('./pages/admin/forms/AdminFormsLayout'))
const AdminFormsIndex = lazy(() => import('./pages/admin/forms/AdminFormsIndex'))
const AdminContactSubmissions = lazy(() => import('./pages/admin/forms/AdminContactSubmissions'))
const AdminContactSubmissionDetail = lazy(() => import('./pages/admin/forms/AdminContactSubmissionDetail'))
const FormModuleGuard = lazy(() => import('./pages/admin/forms/FormModuleGuard'))
const AdminRecruitmentLayout = lazy(() => import('./pages/admin/recruitment/AdminRecruitmentLayout'))
const RecruitmentIndexRedirect = lazy(() =>
  import('./pages/admin/recruitment/AdminRecruitmentLayout').then((module) => ({
    default: module.RecruitmentIndexRedirect,
  })),
)
const AdminJobApplications = lazy(() => import('./pages/admin/recruitment/AdminJobApplications'))
const AdminJobApplicationDetail = lazy(() => import('./pages/admin/recruitment/AdminJobApplicationDetail'))
const AdminJobDepartments = lazy(() => import('./pages/admin/recruitment/AdminJobDepartments'))
const AdminJobStatuses = lazy(() => import('./pages/admin/recruitment/AdminJobStatuses'))
const AdminRecruitmentSettings = lazy(() => import('./pages/admin/recruitment/AdminRecruitmentSettings'))
const RecruitmentModuleGuard = lazy(() => import('./pages/admin/recruitment/RecruitmentModuleGuard'))
const AdminStoryCardsLayout = lazy(() => import('./pages/admin/story-cards/AdminStoryCardsLayout'))
const StoryCardsIndexRedirect = lazy(() =>
  import('./pages/admin/story-cards/AdminStoryCardsLayout').then((module) => ({
    default: module.StoryCardsIndexRedirect,
  })),
)
const AdminStoryCards = lazy(() => import('./pages/admin/story-cards/AdminStoryCards'))
const AdminAiAssistantLayout = lazy(() => import('./pages/admin/ai-assistant/AdminAiAssistantLayout'))
const AiAssistantIndexRedirect = lazy(() =>
  import('./pages/admin/ai-assistant/AdminAiAssistantLayout').then((module) => ({
    default: module.AiAssistantIndexRedirect,
  })),
)
const AdminAiKnowledge = lazy(() => import('./pages/admin/ai-assistant/AdminAiKnowledge'))
const AdminAiAnalytics = lazy(() => import('./pages/admin/ai-assistant/AdminAiAnalytics'))
const AdminWeeklySchedule = lazy(() => import('./pages/admin/AdminWeeklySchedule'))

function AdminSuspense({ children }) {
  return <Suspense fallback={<LoadingSpinner />}>{children}</Suspense>
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <ScrollToTop />
        <LanguageProvider>
          <Routes>
            {/* Legacy ASP.NET website URLs */}
            <Route path="en/default.aspx" element={<Navigate to="/" replace />} />
            <Route path="ar/default.aspx" element={<Navigate to="/" replace />} />
            <Route path="Default.aspx" element={<Navigate to="/" replace />} />
            <Route path="en" element={<Navigate to="/" replace />} />
            <Route path="ar" element={<Navigate to="/" replace />} />

            <Route element={<MainLayout transparentHeader />}>
              <Route index element={<Home />} />
              <Route path="programs" element={<ProgramsPage />} />
              <Route path="pages/programs" element={<Navigate to="/programs" replace />} />
              {REMOVED_PAGE_SLUGS.map((slug) => (
                <Route
                  key={slug}
                  path={`pages/${slug}`}
                  element={<Navigate to="/" replace />}
                />
              ))}
              {Object.entries(LegacyPageSlugRedirects).map(([legacy, slug]) => (
                <Route
                  key={legacy}
                  caseSensitive
                  path={`pages/${legacy}`}
                  element={
                    <Navigate
                      to={REMOVED_PAGE_SLUGS.includes(slug) ? '/' : `/pages/${slug}`}
                      replace
                    />
                  }
                />
              ))}
              <Route path={`pages/${PageSlugs.schoolsByProgram}`} element={<Navigate to="/programs" replace />} />
              <Route path={`pages/${PageSlugs.accreditations}`} element={<AccreditationsPage />} />
              <Route path={`pages/${PageSlugs.weeklySchedule412}`} element={<WeeklySchedulePage />} />
              <Route path={`pages/${PageSlugs.campuses}`} element={<CampusesPage />} />
              <Route
                path={`pages/${PageSlugs.branches}`}
                element={<Navigate to={`/pages/${PageSlugs.campuses}`} replace />}
              />
              <Route path="pages/:slug" element={<PageTemplate />} />
              <Route path="news" element={<NewsList />} />
              <Route path="News" element={<Navigate to="/news" replace />} />
              <Route path="news/:slug" element={<NewsDetail />} />
              <Route path="events" element={<EventsList />} />
              <Route path="events/:slug" element={<EventDetail />} />
              <Route path="gallery" element={<GalleryPage />} />
              <Route path="contact" element={<ContactPage />} />
              <Route path="apply" element={<ApplyPage />} />
              <Route path="counseling" element={<CounselingPage />} />
              <Route path="careers" element={<CareersListPage />} />
              <Route path="jobs" element={<Navigate to="/careers" replace />} />
              <Route path="careers/:slug" element={<Navigate to="/careers" replace />} />
            </Route>

            <Route path="admin/*" element={<Navigate to="/" replace />} />

            <Route path="cp-login" element={<Outlet />}>
              <Route
                index
                element={
                  <AdminSuspense>
                    <AdminLogin />
                  </AdminSuspense>
                }
              />
              <Route
                element={
                  <AdminSuspense>
                    <AdminLayout />
                  </AdminSuspense>
                }
              >
                <Route path="dashboard" element={<AdminDashboard />} />
              <Route path="pages" element={<AdminPages />} />
              <Route path="homepage" element={<AdminHomepage />} />
              <Route path="news" element={<AdminNews />} />
              <Route path="events" element={<AdminEvents />} />
              <Route path="sliders" element={<AdminSliders />} />
              <Route path="gallery" element={<AdminGallery />} />
              <Route path="downloads" element={<Navigate to={adminPath('pages')} replace />} />
              <Route path="story-cards" element={<AdminStoryCardsLayout />}>
                <Route index element={<StoryCardsIndexRedirect />} />
                <Route path=":cardType" element={<AdminStoryCards />} />
              </Route>
              <Route path="menus" element={<AdminMenus />} />
              <Route path="recruitment" element={<AdminRecruitmentLayout />}>
                <Route index element={<RecruitmentIndexRedirect />} />
                <Route path="vacancies" element={<Navigate to={adminPath('recruitment/applications')} replace />} />
                <Route
                  path="applications"
                  element={
                    <RecruitmentModuleGuard module="recruitment_applications">
                      <AdminJobApplications />
                    </RecruitmentModuleGuard>
                  }
                />
                <Route
                  path="applications/:id"
                  element={
                    <RecruitmentModuleGuard module="recruitment_applications">
                      <AdminJobApplicationDetail />
                    </RecruitmentModuleGuard>
                  }
                />
                <Route
                  path="departments"
                  element={
                    <RecruitmentModuleGuard module="recruitment_departments">
                      <AdminJobDepartments />
                    </RecruitmentModuleGuard>
                  }
                />
                <Route
                  path="statuses"
                  element={
                    <RecruitmentModuleGuard module="recruitment_statuses">
                      <AdminJobStatuses />
                    </RecruitmentModuleGuard>
                  }
                />
                <Route
                  path="settings"
                  element={
                    <RecruitmentModuleGuard module="recruitment_settings">
                      <AdminRecruitmentSettings />
                    </RecruitmentModuleGuard>
                  }
                />
              </Route>
              <Route path="forms" element={<AdminFormsLayout />}>
                <Route index element={<AdminFormsIndex />} />
                <Route
                  path="applications"
                  element={
                    <FormModuleGuard module="forms_applications">
                      <AdminApplications />
                    </FormModuleGuard>
                  }
                />
                <Route
                  path="applications/:id"
                  element={
                    <FormModuleGuard module="forms_applications">
                      <AdminApplicationDetail />
                    </FormModuleGuard>
                  }
                />
                <Route
                  path="counseling"
                  element={
                    <FormModuleGuard module="forms_counseling">
                      <AdminCounseling />
                    </FormModuleGuard>
                  }
                />
                <Route
                  path="counseling/:id"
                  element={
                    <FormModuleGuard module="forms_counseling">
                      <AdminCounselingDetail />
                    </FormModuleGuard>
                  }
                />
                <Route
                  path="contact"
                  element={
                    <FormModuleGuard module="forms_contact">
                      <AdminContactSubmissions />
                    </FormModuleGuard>
                  }
                />
                <Route
                  path="contact/:id"
                  element={
                    <FormModuleGuard module="forms_contact">
                      <AdminContactSubmissionDetail />
                    </FormModuleGuard>
                  }
                />
              </Route>
              <Route path="settings" element={<AdminSettings />} />
              <Route path="ai-assistant" element={<AdminAiAssistantLayout />}>
                <Route index element={<AiAssistantIndexRedirect />} />
                <Route path="knowledge" element={<AdminAiKnowledge />} />
                <Route path="analytics" element={<AdminAiAnalytics />} />
              </Route>
              <Route path="weekly-schedule" element={<AdminWeeklySchedule />} />
              <Route path="users" element={<AdminUsers />} />
              <Route path="activity-logs" element={<AdminActivityLogs />} />
              <Route path="profile" element={<AdminProfile />} />
              <Route path="applications" element={<Navigate to={adminPath('forms/applications')} replace />} />
              <Route path="counseling" element={<Navigate to={adminPath('forms/counseling')} replace />} />
            </Route>
            </Route>
          </Routes>
        </LanguageProvider>
      </BrowserRouter>
    </AuthProvider>
  )
}
